import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Server } from 'shared-types';
import { ChevronDownIcon, CheckIcon, MapPinIcon } from 'shared-icons';

interface ServerSelectorProps {
  servers: Server[];
  selectedServer: Server;
  onSelectServer: (server: Server) => void;
  isLoading: boolean;
  isManual: boolean;
  onSetManual: (manual: boolean) => void;
  onAddCustom: () => void;
  isDisabled: boolean;
}

const ServerSelector: React.FC<ServerSelectorProps> = ({
  servers,
  selectedServer,
  onSelectServer,
  isLoading,
  isManual,
  onSetManual,
  onAddCustom,
  isDisabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const tierOrder = { gold: 1, silver: 2, bronze: 3 };

  const groupedAndSortedServers = useMemo(() => {
    const groups: { [key: string]: Server[] } = {};

    servers.forEach(server => {
      const country = server.country || 'Custom';
      let region = 'Other';

      if (['Pakistan'].includes(country)) region = 'Pakistan';
      else if (['United Arab Emirates', 'India'].includes(country)) region = 'Middle East & South Asia';
      else if (['Singapore', 'Japan', 'Hong Kong', 'Australia'].includes(country)) region = 'Asia & Oceania';
      else if (['Germany', 'Netherlands', 'United Kingdom', 'France'].includes(country)) region = 'Europe';
      else if (['United States', 'Canada'].includes(country)) region = 'North America';
      else if (['Brazil', 'South Africa'].includes(country)) region = 'South America & Africa';
      else if (country === 'Custom') region = 'Custom';

      if (!groups[region]) groups[region] = [];
      groups[region].push(server);
    });

    // Sort servers within each group: sponsored first, then by name
    for (const region in groups) {
      groups[region].sort((a, b) => {
        const tierA = a.isSponsored ? tierOrder[a.sponsorshipTier || 'bronze'] : 99;
        const tierB = b.isSponsored ? tierOrder[b.sponsorshipTier || 'bronze'] : 99;
        if (tierA !== tierB) return tierA - tierB;
        return a.name.localeCompare(b.name);
      });
    }

    const regionOrder = ['Pakistan', 'Middle East & South Asia', 'Asia & Oceania', 'Europe', 'North America', 'South America & Africa', 'Custom', 'Other'];
    const orderedGroups: { [key: string]: Server[] } = {};
    regionOrder.forEach(region => {
        if (groups[region]) orderedGroups[region] = groups[region];
    });

    Object.keys(groups).forEach(region => {
        if (!orderedGroups[region]) orderedGroups[region] = groups[region];
    });

    return orderedGroups;
  }, [servers]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (server: Server) => {
    onSelectServer(server);
    onSetManual(true);
    setIsOpen(false);
  };

  const handleMainButtonClick = () => {
    if (isDisabled) return;
    if (!isManual) onSetManual(true);
    setIsOpen(!isOpen);
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-2">
        <label className="text-lg font-semibold text-slate-900 dark:text-slate-100">Test Server</label>
        <div className={`flex rounded-full p-1 bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm ring-1 ring-black/5 dark:ring-white/10 transition-opacity ${isDisabled ? 'opacity-50' : ''}`}>
            <button onClick={() => !isDisabled && onSetManual(false)} disabled={isDisabled} className={`flex-1 py-1 px-3 text-center rounded-full font-semibold text-sm transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${!isManual ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white shadow-md' : 'text-slate-600 dark:text-slate-300 hover:bg-gray-300/50 dark:hover:bg-slate-600/50'}`} aria-pressed={!isManual}>Auto</button>
            <button onClick={() => !isDisabled && (onSetManual(true), setIsOpen(true))} disabled={isDisabled} className={`flex-1 py-1 px-3 text-center rounded-full font-semibold text-sm transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 ${isManual ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white shadow-md' : 'text-slate-600 dark:text-slate-300 hover:bg-gray-300/50 dark:hover:bg-slate-600/50'}`} aria-pressed={isManual}>Manual</button>
        </div>
      </div>
      
      <div className="relative" ref={wrapperRef}>
        <button
          onClick={handleMainButtonClick}
          disabled={isDisabled}
          className="w-full bg-white/20 dark:bg-slate-800/20 backdrop-blur-sm border border-white/30 dark:border-slate-700/50 text-slate-800 dark:text-white rounded-full p-3 text-left flex items-center justify-between transition-opacity disabled:opacity-60 disabled:cursor-not-allowed"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          {isLoading && !isManual ? (
            <div className="flex items-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-500"></div><span>Finding optimal server...</span></div>
          ) : (
            <div className="flex items-center gap-3">
              {selectedServer.isSponsored && selectedServer.ispLogoUrl && (
                  <img src={selectedServer.ispLogoUrl} alt={`${selectedServer.name} Logo`} className="h-8 w-8 object-contain bg-white rounded-full p-1" />
              )}
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-base font-bold text-slate-900 dark:text-slate-100">{selectedServer.name}</p>
                  {selectedServer.isSponsored && <span className="text-xs font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-100 dark:bg-cyan-900/50 px-2 py-0.5 rounded-full">Sponsored</span>}
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400 mt-1">
                  <span>{selectedServer.city}, {selectedServer.country}</span>
                  {selectedServer.distance != null && (
                    <>
                      <span className="text-slate-300 dark:text-slate-600">&bull;</span>
                      <span className="flex items-center gap-1">
                        <MapPinIcon width={14} height={14} />
                        {Math.round(selectedServer.distance)} km
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          <ChevronDownIcon isOpen={isOpen} className="w-6 h-6" />
        </button>

        {isOpen && isManual && (
          <ul className="absolute z-10 w-full mt-1 bg-white/80 dark:bg-slate-800/80 backdrop-blur-md border border-white/30 dark:border-slate-700/50 rounded-lg shadow-lg max-h-60 overflow-y-auto" role="listbox">
            {Object.entries(groupedAndSortedServers).map(([region, regionServers]) => (
              <React.Fragment key={region}>
                <li className="px-4 py-2 text-xs font-bold uppercase text-gray-500 dark:text-slate-400 bg-gray-50/80 dark:bg-slate-700/60 backdrop-blur-sm sticky top-0" role="presentation">{region}</li>
                {Array.isArray(regionServers) && regionServers.map(server => (
                  <li key={server.id}>
                    <button onClick={() => handleSelect(server)} className="w-full text-left px-4 py-3 hover:bg-gray-100/50 dark:hover:bg-slate-700/50 flex justify-between items-center" role="option" aria-selected={selectedServer.id === server.id}>
                      <div className="flex items-center gap-3">
                        {server.isSponsored && server.ispLogoUrl && <img src={server.ispLogoUrl} alt={`${server.name} Logo`} className="h-8 w-8 object-contain bg-white rounded-full p-1" />}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-slate-800 dark:text-slate-200">{server.name}</p>
                            {server.isSponsored && <span className="text-xs font-bold text-cyan-600 dark:text-cyan-400 bg-cyan-100 dark:bg-cyan-900/50 px-2 py-0.5 rounded-full">Sponsored</span>}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-slate-400">{server.city}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {server.distance != null && (
                            <span className="text-sm text-gray-500 dark:text-slate-400 font-medium flex items-center gap-1">
                                <MapPinIcon width={14} height={14} />
                                {Math.round(server.distance)} km
                            </span>
                        )}
                        {server.ping && server.ping < 9999 && <span className="text-sm text-cyan-600 dark:text-cyan-400 font-medium">{server.ping} ms</span>}
                        {selectedServer.id === server.id && <CheckIcon className="text-cyan-500" />}
                      </div>
                    </button>
                  </li>
                ))}
              </React.Fragment>
            ))}
            <li><button onClick={onAddCustom} className="w-full text-left px-4 py-3 text-cyan-600 dark:text-cyan-400 font-semibold hover:bg-gray-100/50 dark:hover:bg-slate-700/50 border-t border-gray-200 dark:border-slate-700">+ Add Custom Server</button></li>
          </ul>
        )}
      </div>
    </div>
  );
};

export default ServerSelector;